<template>
  <div class="grid items-end gap-x-4 transition-all will-change-contents">
    <div class="shadow-all-0030-black-50 rounded-16">
      <div class="border-b border-bor-nav p-2">
        <template v-if="forwardings.length <= 3">
          <forward-preview
            v-for="forwarding in forwardings"
            :key="forwarding.forwardingEventId!"
            :forwarding="forwarding"
          />
        </template>
        <template v-else>
          {{ countForwardingsText }} {{ $t('chat.forForwarding') }}
        </template>
      </div>

      <div
        v-if="modelFiles.length"
        class="flex flex-wrap gap-2 border-b border-bor-nav px-4 py-2"
      >
        <div
          v-for="(file, index) in modelFiles"
          :key="`${getFileDisplayName(file)}-${index}`"
          class="flex items-center gap-1 rounded-12 bg-bg-base-bg3 px-2 py-1 text-1324 text-fg-base-fg3"
        >
          <span class="max-w-24 truncate">{{ getFileDisplayName(file) }}</span>
          <div class="relative inline-flex h-6 w-6 shrink-0 items-center justify-center">
            <loader-circle
              v-if="!getFileError(file)"
              :progress="getFileProgress(file)"
            />
            <wui-btn
              icon
              variant="neut"
              state="alpha"
              prepend-icon="closeRegularMdM"
              data-test="forward-message-remove-file"
              @click="removeFile(index)"
            >
              <span class="sr-only">{{ $t('common.removeFile') }}</span>
            </wui-btn>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-[auto_1fr] items-center gap-x-1 pr-2 pl-1 py-1 transition-all will-change-contents">
        <div>
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept="*/*"
            multiple
            data-test="forward-message-file-input"
            @change="onFilePicked"
          >
          <wui-btn
            variant="neut"
            state="alpha"
            prepend-icon="paperclipM"
            icon
            data-test="forward-message-attach-file"
            @click="onPickFile"
          >
            <span class="sr-only">{{ $t('common.attachFile') }}</span>
          </wui-btn>
        </div>
        <content-editable
          id="contenteditable"
          v-model="messageText"
          class="
            my-3.5 mr-3.5 ml-1 max-h-[25vh] overflow-y-auto whitespace-pre-wrap
            text-1420 tracking-[0.4px] outline-none will-change-scroll
            "
          no-h-t-m-l
          @input="$emit('input', messageText)"
          @file-picked="onFilePickedFromPaste"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn, useRenderNotifier } from '@wui/common-library'
import {
  HistoryMessageItem,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  IMForwarding,
} from '@wui/im'
import { computed, ref, unref } from 'vue'

import { countMessagesText, getForwardingAuthorName, ForwardPreview } from '@/entities/messages'

import { ContentEditable } from '@/shared/components/content-editable'
import { LoaderCircle } from '@/shared/components/loaders'
import { useLocalization } from '@/shared/composables/localization'
import { MAX_UPLOAD_FILES_COUNT } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'

const props = withDefaults(
  defineProps<{
    events: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>[]
    files?: (File | ChatFile)[]
  }>(),
  { files: () => [] },
)

const emit = defineEmits<{
  (event: 'input', value: string): void
  (event: 'add-files', value: File[]): void
  (event: 'remove-file', index: number): void
}>()

const modelFiles = computed(() => props.files ?? [])
const fileInputRef = ref<HTMLInputElement | null>(null)

const getFileDisplayName = (file: File | ChatFile): string =>
  file && 'file' in file ? (file as ChatFile).file.name : (file as File).name

const getFileProgress = (file: File | ChatFile): number =>
  file && 'progress' in file ? unref((file as ChatFile).progress) : 100

const getFileError = (file: File | ChatFile): boolean =>
  file && 'isError' in file ? unref((file as ChatFile).isError) : false

const { notifyError } = useRenderNotifier()
const { t } = useLocalization()

const onPickFile = () => {
  fileInputRef.value?.click()
}

const addFilesFromList = (fileList: FileList | null) => {
  if (!fileList?.length) return
  const totalCountOfFiles = modelFiles.value.length + fileList.length
  if (totalCountOfFiles >= MAX_UPLOAD_FILES_COUNT) {
    notifyError(t('common.maxFilesError'))
    return
  }
  emit('add-files', Array.from(fileList))
}

const onFilePicked = (event: Event) => {
  const input = event.target as HTMLInputElement
  const fileList = input.files
  if (!fileList?.length) return
  addFilesFromList(fileList)
  input.value = ''
}

const onFilePickedFromPaste = (fileList: FileList) => {
  addFilesFromList(fileList)
}

const removeFile = (index: number) => {
  emit('remove-file', index)
}

const messageText = ref('')

const forwardings = computed<IMForwarding[]>(() =>
  props.events.map(e => ({
    forwardingEventId: e.eventId,
    forwardingText: e.item.messageText,
    forwardingChatPServed: e.item.pServed,
    forwardingAuthorName: getForwardingAuthorName(e),
    forwardingFiles: e.item.files!,
  })),
)

const countForwardingsText = computed(() => {
  return countMessagesText(forwardings.value.length)
})
</script>
